/**
 * describeLock.js — THE one answer to "may the patient still change their Describe answers?"
 *
 * ★★★ s98 (v242.244.0) · D460 — Dr. Rami, 2026-10-07: THE ANSWERS STAY EDITABLE UNTIL A
 *     DOCTOR ACCEPTS THE CASE. Sent but not accepted is still editable (the doctor sees
 *     "Updated by the patient"); accepted or later is final, and corrections go as a note.
 *     The summary is rebuilt ONCE after an edit (the server compares its answers
 *     fingerprint) and never on a revisit. The server is the control (orella-api
 *     _answers_locked + update_case's 409 answers_locked); this mirrors its status set.
 *     The s96 notes below describe the rule this replaced — the "View my summary" tap and
 *     describeComplete no longer lock anything.
 *
 * ★★★ THE LOCK IS THE PATIENT'S "VIEW MY SUMMARY" TAP (POST /cases/<id>/summary/lock ->
 *     cases.summary_locked_at), NOT "a summary exists". The server prewarms the summary the
 *     moment the last answers land, before "Check your answers"; locking on its arrival would
 *     freeze the patient out of their own review mid-edit. Locked + written = final on the
 *     server (orella-api _patient_summary_final). describeComplete still counts as locked.
 *
 * ★★ v242.235.0 (session 96) — Dr. Rami's mockup-B ruling: ANSWERS LOCK ONCE THE SUMMARY IS
 *    WRITTEN, and the summary is written ONCE. Before this, the lock was `describeComplete`,
 *    which is set only when the patient taps Submit AFTER reading the summary — so a patient
 *    could open the summary, go back, change answers, and leave a summary that no longer
 *    described them (and, server side, a regenerate could then overwrite what they read).
 *    Corrections after the summary go to the doctor as a note, never into the answers.
 *
 * hasWrittenSummary mirrors the server's write-once test (_patient_summary_written in
 * orella-api): a usable AI summary — at least 3 differential entries and a narrative — that
 * is not the non-AI placeholder. Language is deliberately NOT part of it: a written summary
 * stays in the language it was written in (ruling: no regenerate on a language switch).
 *
 * Every Describe screen that edits answers asks isDescribeLocked; none spells the test.
 */
export function hasWrittenSummary(caseObj) {
  if (!caseObj) return false
  let ps = caseObj.patientSummary || caseObj.patient_summary
  if (typeof ps === 'string') {
    try { ps = JSON.parse(ps) } catch { return false }
  }
  if (!ps || typeof ps !== 'object') return false
  if (ps.isAIGenerated === false) return false
  const dx = ps.differentialDx || ps.conditionsToDiscuss || []
  const narrative = ps.patientNarrative || ps.summary || ''
  return Array.isArray(dx) && dx.length >= 3 && String(narrative).trim() !== ''
}

// orella-api _ANSWERS_LOCKED_STATUSES, the same ten values.
export const ANSWERS_LOCKED_STATUSES = new Set([
  'claimed', 'scheduled', 'checked_in', 'in_progress', 'completed', 'finalized',
  'documented', 'in_follow_up', 'in_observation', 'observation',
])

export function isDescribeLocked(caseObj) {
  if (!caseObj) return false
  return ANSWERS_LOCKED_STATUSES.has(String(caseObj.status || ''))
}
